import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert } from 'reactstrap';
import { confirmCODPayment } from '../../utils/order';
import { getLoggedinUser } from '../../utils/api_helper_custom';

const CODPaymentConfirmation = ({ 
  orderId, 
  orderReferenceId, 
  isOpen, 
  toggle, 
  onConfirm, 
  onError 
}) => {
  const [notes, setNotes] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState(null);

  // Check if current user can confirm COD payments
  const canConfirmCOD = () => {
    const currentUser = getLoggedinUser();
    if (!currentUser) return false;
    
    const allowedRoles = ['employee2', 'employee3', 'superadmin'];
    return allowedRoles.includes(currentUser.role || currentUser.user_type);
  };

  const handleConfirm = async () => {
    if (!canConfirmCOD()) {
      setError('You do not have permission to confirm COD payments.');
      return;
    }

    if (!window.confirm(`Are you sure you want to confirm COD payment collection for Order ${orderReferenceId}?`)) {
      return;
    }

    setIsConfirming(true);
    setError(null);
    
    try {
      const response = await confirmCODPayment(orderId, notes);
      
      if (response.success) {
        // Success - call the onConfirm callback
        onConfirm && onConfirm(response.order || response.order_data);
        
        // Reset form
        setNotes('');
        setError(null);
        
        // Close modal
        toggle();
        
        // Show success message
        if (window.toast) {
          window.toast.success('COD payment confirmed successfully!');
        } else {
          alert('COD payment confirmed successfully!');
        }
      } else {
        setError(response.message || 'Failed to confirm COD payment');
        onError && onError(response.message);
      }
    } catch (error) {
      const errorMessage = error.message || 'Network error occurred';
      setError(errorMessage);
      onError && onError(errorMessage);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    setNotes('');
    setError(null);
    toggle();
  };

  if (!canConfirmCOD()) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="md">
        <ModalHeader toggle={toggle}>Access Denied</ModalHeader>
        <ModalBody>
          <Alert color="danger">
            You do not have permission to confirm COD payments. 
            Only Employee2, Employee3, and SuperAdmin can perform this action.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancel}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        Confirm COD Payment Collection
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <h6>Order Details:</h6>
          <p><strong>Order ID:</strong> {orderReferenceId}</p>
          <p><strong>Order Number:</strong> #{orderId}</p>
        </div>

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form>
          <FormGroup>
            <Label for="cod-notes">
              Delivery Notes <span className="text-muted">(Optional)</span>
            </Label>
            <Input
              type="textarea"
              id="cod-notes"
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Cash collected by John Doe at 2:30 PM"
              rows={3}
            />
            <small className="form-text text-muted">
              Add any notes about the payment collection process.
            </small>
          </FormGroup>
        </Form>

        <Alert color="warning" className="mt-3">
          <strong>Warning:</strong> This action will confirm that the COD payment has been collected 
          and will update the order status. This action cannot be undone.
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button 
          color="secondary" 
          onClick={handleCancel}
          disabled={isConfirming}
        >
          Cancel
        </Button>
        <Button 
          color="success" 
          onClick={handleConfirm}
          disabled={isConfirming}
        >
          {isConfirming ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Confirming...
            </>
          ) : (
            'Confirm Payment Collection'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CODPaymentConfirmation;
